/**
 * `<SeasonalityView>` — a Seasonality screen (`docs/features/seasonality.md`,
 * mock `docs/seasonality-layout-mock.html`).
 *
 * Rows are **windows, not symbols**: a start date and a length, from the yearly
 * store, over the base screen's symbols. The base screen is rendered as an
 * ordinary `ScreenerView` so its bar — the group chips, which *are* the
 * universe (owner, 2026-10-22) — and its members work as on any screen; its
 * grid is never drawn, `frameGrid` puts this panel's filter row and grid there.
 *
 * Basic (`ind:Seasonality`, group `qf-seasonality`) is basis D, the Trade
 * ranking, Hit and Avg; Pro (`ind:SeasonalityPro`, group `qf-seas-pro`) adds
 * W / M, Season, Xs % and X Sharpe. The server answers each tier with its own
 * fields whatever is asked.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { ScreenerConfig, ScreenerSeasonality } from "@qufolio/model";
import type { PresetDefinition, ScreenerConfigPatch } from "@qufolio/screener-grid";

import { fetchPreset, saveScreen } from "../api/presets";
import { fetchWindows, seasonalityConfigured, type SeasWindow, type WindowsAnswer } from "../api/seasonality";
import { useEntitlements } from "../lib/entitlements";
import {
  calendarCells,
  effective,
  PERIOD_RANGE,
  floorsChip,
  rollNote,
  settingsChip,
  shortDate,
  sortWindows,
  startCell,
  startChip,
  tierOf,
  UNIT,
  type SortKey,
} from "../lib/seasonality-screen";
import { isOwn, useScreens } from "../lib/screens";
import { pickWindow } from "../lib/seasonality-detail";
import type { MemberRow } from "../lib/topdown";
import { ScreenerView } from "./ScreenerView";

export interface SeasonalityViewProps {
  config: ScreenerConfig & { seasonality: ScreenerSeasonality };
  side?: "left" | "right" | "top" | "bottom";
  onSelectSymbol?: (symbol: string) => void;
  onConfigChange?: (patch: ScreenerConfigPatch) => void;
  onHide?: () => void;
  onNewScreen?: () => void;
  onEditScreen?: () => void;
}

/** The start date "today" moves at midnight; ask again now and then. */
const REFRESH_MS = 10 * 60_000;
const SETTLE_MS = 400;
const NO_TICKS = { sector: [] as string[], industry: [] as string[] };

export function SeasonalityView({
  config, side, onSelectSymbol, onConfigChange, onHide, onNewScreen, onEditScreen,
}: SeasonalityViewProps) {
  const seas = config.seasonality;
  const { can } = useEntitlements();
  const tier = tierOf(can);
  const shown = useMemo(() => effective(seas, tier), [seas, tier]);
  const { screens } = useScreens();
  const self = screens.find((s) => s.key === config.screenRef);
  const baseSummary = screens.find((s) => s.key === seas.base);

  // ── the base screen, live (TopDown's rule) ───────────────────────────────
  const [base, setBase] = useState<PresetDefinition | null | "missing">(null);
  const baseVersion = baseSummary?.version;
  useEffect(() => {
    let live = true;
    setBase(null);
    void fetchPreset(seas.base).then((found) => {
      if (live) setBase(found ?? "missing");
    });
    return () => {
      live = false;
    };
  }, [seas.base, baseVersion]);
  const { id, group, screen, extendedHours, savedFilters, filter, activeFilterRefs, activeFilterRef } = config;
  const baseConfig = useMemo<ScreenerConfig | null>(() => {
    if (!base || base === "missing") return null;
    return {
      id, group, screen,
      ...(extendedHours !== undefined ? { extendedHours } : {}),
      columns: base.columns ?? [],
      ...(filter ? { filter } : {}),
      sort: base.sort ?? [],
      savedFilters: base.savedFilters ?? savedFilters ?? [],
      savedFilterCategoryOrder: base.savedFilterCategoryOrder ?? [],
      activeFilterRefs: activeFilterRefs ?? [],
      ...(activeFilterRef ? { activeFilterRef } : {}),
    };
  }, [base, id, group, screen, extendedHours, savedFilters, filter, activeFilterRefs, activeFilterRef]);

  // ── members ──────────────────────────────────────────────────────────────
  const [symbols, setSymbols] = useState<string[]>([]);
  const sig = useRef("");
  const onMembers = useCallback((rows: MemberRow[]) => {
    const next = rows.map((r) => r.symbol).sort();
    const key = next.join(",");
    if (key === sig.current) return;
    sig.current = key;
    setSymbols(next);
  }, []);
  const membersProp = useMemo(() => ({ ticks: NO_TICKS, onMembers, conditionsScreen: seas.base }), [onMembers, seas.base]);

  // ── windows ──────────────────────────────────────────────────────────────
  const [answer, setAnswer] = useState<WindowsAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setPulse((n) => n + 1), REFRESH_MS);
    return () => window.clearInterval(t);
  }, []);
  const askKey = useMemo(() => JSON.stringify([symbols.join(","), shown]), [symbols, shown]);
  useEffect(() => {
    if (!seasonalityConfigured || symbols.length === 0 || tier === null) return;
    const abort = new AbortController();
    const timer = window.setTimeout(() => {
      void fetchWindows(symbols, shown, abort.signal).then((result) => {
        if (abort.signal.aborted) return;
        if (result.ok) {
          setAnswer(result.answer);
          setError(null);
        } else setError(result.reason);
      });
    }, SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
      abort.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askKey, pulse, tier]);

  // ── the definition, back to its own screen (TopDownView's rule) ──────────
  const definition = useMemo(() => JSON.stringify(seas), [seas]);
  const saved = useRef(definition);
  useEffect(() => {
    if (!self || !isOwn(self) || definition === saved.current) return;
    const timer = window.setTimeout(() => {
      saved.current = definition;
      void saveScreen(self.key, {
        name: self.name,
        market: self.market,
        groupCodes: self.groupCodes,
        seasonality: JSON.parse(definition) as ScreenerSeasonality,
      }).catch(() => {
        saved.current = "";
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [definition, self]);

  const set = useCallback(
    (patch: Partial<ScreenerSeasonality>) => onConfigChange?.({ seasonality: { ...seas, ...patch } }),
    [seas, onConfigChange],
  );

  const [selected, setSelected] = useState<string | null>(null);
  const pick = (w: SeasWindow) => {
    setSelected(rowKey(w));
    // The Data Panel's Seasonality tab describes the row picked here.
    pickWindow(w, shown.basis, shown.years);
    onSelectSymbol?.(w.symbol);
  };

  const content = (
    <div style={BODY}>
      <FilterRow seas={shown} tier={tier} answer={answer} set={set} />
      <div style={COUNT}>
        {error ? (
          <span>Windows: {error}</span>
        ) : answer ? (
          <span>
            {countLine(answer, shown.mode)}
            {answer.truncated && " · first 5,000"}
          </span>
        ) : (
          <span>{symbols.length ? "Searching…" : "Waiting for the base screen's rows…"}</span>
        )}
        <span style={HINT}>click a row → active chart</span>
      </div>
      {answer && shown.mode === "cal" ? (
        <Calendar answer={answer} unit={UNIT[shown.basis] ?? "td"} selected={selected} onPick={pick} />
      ) : (
        <WindowGrid rows={answer?.rows ?? []} pro={tier === "pro"} rank={shown.rank} unit={UNIT[shown.basis] ?? "td"}
          selected={selected} onPick={pick} />
      )}
    </div>
  );

  const baseName = baseSummary?.name ?? (base && base !== "missing" ? base.name : seas.base);
  return (
    <div style={ROOT}>
      {base === "missing" ? (
        // A base nobody can open — deleted, or a stale reference when this was
        // made. A choice rather than a dead end: the screen keeps its settings.
        <div style={NOTICE}>
          <div style={{ marginBottom: 6 }}>The screen this one searches over is gone. Pick another:</div>
          <select
            style={INPUT}
            value=""
            onChange={(e) => e.target.value && set({ base: e.target.value })}
            aria-label="Base screen"
          >
            <option value="">Choose a screen…</option>
            {screens
              .filter((s) => !s.seasonality && !s.topDown && !s.recs)
              .map((s) => (
                <option key={s.key} value={s.key}>
                  {s.name}
                </option>
              ))}
          </select>
        </div>
      ) : baseConfig ? (
        <ScreenerView
          config={baseConfig}
          onSelectSymbol={onSelectSymbol}
          topDown={membersProp}
          screenHost={{ config, onConfigChange }}
          frameGrid={() => content}
          onHide={onHide}
          onNewScreen={onNewScreen}
          onEditScreen={onEditScreen}
          dockSide={side}
        />
      ) : (
        <div style={NOTICE}>Loading {baseName}…</div>
      )}
    </div>
  );
}

function rowKey(w: SeasWindow): string {
  return `${w.symbol}|${w.dir}|${w.start}|${w.len}`;
}

function countLine(a: WindowsAnswer, mode: string): string {
  const syms = new Set(a.rows.map((r) => r.symbol)).size;
  const roll = rollNote(a.asked, a.from, a.rolled);
  const head = mode === "cal" ? `Window starts · 4 weeks from ${shortDate(a.from)}` : `${a.rows.length} windows · ${syms} symbols`;
  return roll ? `${head} · ${roll}` : head;
}

// ── the filter row: modes + three chips ───────────────────────────────────

function FilterRow({
  seas, tier, answer, set,
}: {
  seas: ReturnType<typeof effective>;
  tier: ReturnType<typeof tierOf>;
  answer: WindowsAnswer | null;
  set: (patch: Partial<ScreenerSeasonality>) => void;
}) {
  const [open, setOpen] = useState<"settings" | "floors" | "start" | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);
  const pro = tier === "pro";
  const toggle = (k: typeof open) => setOpen((o) => (o === k ? null : k));
  return (
    <div ref={ref} style={ROW} role="toolbar" aria-label="Seasonality filters">
      <Seg
        value={seas.mode}
        options={[
          ["soon", "Soon", undefined, `Starting soon: windows that start between the From date and ${seas.within} trading days after it (set in the date chip)`],
          ["now", "Now", undefined, "Moving now: windows that started before the From date and have not ended yet"],
          ["cal", "Cal", undefined, "Calendar: windows that start in the next four weeks (20 trading days), laid out by day"],
        ]}
        onChange={(mode) => set({ mode: mode as ScreenerSeasonality["mode"] })}
      />
      <Chip label={settingsChip(seas, tier)} title="Basis, years, period, direction and ranking" open={open === "settings"} onToggle={() => toggle("settings")}>
        <label style={LBL} title="The bar the windows are measured on">Basis</label>
        <Seg value={seas.basis} onChange={(basis) => set({ basis: basis as ScreenerSeasonality["basis"] })}
          options={pro
            ? [["d", "D", undefined, "Daily: windows measured in trading days"], ["w", "W", undefined, "Weekly: windows measured in weeks"], ["m", "M", undefined, "Monthly: windows measured in months"]]
            : [["d", "D", undefined, "Daily: windows measured in trading days"]]} />
        <label style={LBL} title="How many past years the average is taken over. This year is never included">Years</label>
        <Seg value={seas.years} onChange={(years) => set({ years: years as ScreenerSeasonality["years"] })}
          options={[
            ["5", "5", undefined, "The last 5 years"], ["10", "10", undefined, "The last 10 years"],
            ["20", "20", undefined, "The last 20 years"], ["30", "30", undefined, "The last 30 years"],
            ["all", "All", undefined, "Every year of the symbol's history"],
          ]} />
        <label style={LBL} title="The window lengths searched, shortest to longest">Period ({UNIT[seas.basis] ?? "td"})</label>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <NumberBox value={seas.periodMin ?? PERIOD_RANGE[seas.basis]![0]} min={1} max={260} title="Shortest window"
            onChange={(n) => set({ periodMin: Math.round(n) })} />
          <span style={{ color: "var(--qf-text-muted)" }}>to</span>
          <NumberBox value={seas.periodMax ?? PERIOD_RANGE[seas.basis]![1]} min={1} max={260} title="Longest window"
            onChange={(n) => set({ periodMax: Math.round(n) })} />
          <button type="button" style={BTN} title="Search every length" onClick={() => set({ periodMin: undefined, periodMax: undefined })}>All</button>
        </span>
        <label style={LBL} title="Which way the window moved">Direction</label>
        <Seg value={seas.dir} onChange={(dir) => set({ dir: dir as ScreenerSeasonality["dir"] })}
          options={[
            ["both", "Both", undefined, "Long and short windows"],
            ["L", "Long", undefined, "Windows where price usually rose"],
            ["S", "Short", undefined, "Windows where price usually fell"],
          ]} />
        <label style={LBL} title="How the best window per symbol is chosen">Rank</label>
        <Seg value={seas.rank} onChange={(rank) => set({ rank: rank as ScreenerSeasonality["rank"] })}
          options={pro
            ? [["trade", "Trade", undefined, "Trade: the window that went your way most often (Hit), then by size of move"],
               ["season", "Season", undefined, "Season: the window that beat each year's normal move by the most, most steadily (Excess Sharpe)"]]
            : [["trade", "Trade", undefined, "Trade: the window that went your way most often (Hit), then by size of move"]]} />
      </Chip>
      <Chip label={floorsChip(seas)} title="The minimums a window must meet" open={open === "floors"} onToggle={() => toggle("floors")}>
        <label style={LBL} title="Only windows that went the right way in at least this share of years">Hit ≥ %</label>
        <NumberBox value={seas.hitMin} min={0} max={100} onChange={(hitMin) => set({ hitMin })} />
        <label style={LBL} title="Only windows whose average move is at least this size, up or down">|Avg| ≥ %</label>
        <NumberBox value={seas.avgMin} min={0} max={100} step={0.5} onChange={(avgMin) => set({ avgMin })} />
        <label style={LBL} title="Only windows measured over at least this many years">Min years</label>
        <Seg value={String(seas.minYears)} onChange={(v) => set({ minYears: Number(v) as 5 | 10 })}
          options={[["5", "5", undefined, "At least 5 years"], ["10", "10", undefined, "At least 10 years"]]} />
      </Chip>
      <Chip label={startChip(seas, answer?.from ?? null)} title="The date the search counts from" open={open === "start"} onToggle={() => toggle("start")}>
        <label style={LBL} title="A weekend or market holiday rolls to the next session">From</label>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="date" value={seas.from ?? answer?.from ?? ""} style={INPUT}
            onChange={(e) => set({ from: e.target.value || undefined })} aria-label="Start date" />
          <button type="button" style={BTN} title="Count from today" onClick={() => set({ from: undefined })}>Today</button>
        </span>
        {seas.mode === "soon" && (
          <>
            <label style={LBL} title="How many trading days after From a window may start. 0 is From itself">Within trading days</label>
            <NumberBox value={seas.within} min={0} max={60} onChange={(within) => set({ within: Math.round(within) })} />
          </>
        )}
      </Chip>
    </div>
  );
}

function Seg({
  value, options, onChange,
}: {
  value: string;
  /** `[value, label, lock?, hover?]` — a `lock` disables the option and says why. */
  options: [string, string, string?, string?][];
  onChange: (value: string) => void;
}) {
  return (
    <span style={SEG}>
      {options.map(([v, label, lock, hover], i) => (
        <button key={v} type="button" disabled={Boolean(lock)} title={lock ? `${label} — ${lock}` : hover}
          aria-pressed={value === v} onClick={() => onChange(v)}
          style={{ ...SEG_BTN, ...(i > 0 ? SEG_SEP : {}), ...(value === v ? SEG_ON : {}), ...(lock ? SEG_OFF : {}) }}>
          {label}
          {lock && <sup style={{ fontSize: 8, marginLeft: 2 }}>{lock}</sup>}
        </button>
      ))}
    </span>
  );
}

function Chip({ label, title, open, onToggle, children }: { label: string; title?: string; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <span style={{ position: "relative" }}>
      <button type="button" style={{ ...CHIP, ...(open ? CHIP_ON : {}) }} title={title} aria-expanded={open} onClick={onToggle}>
        {label} ▾
      </button>
      {open && <div style={POP}>{children}</div>}
    </span>
  );
}

function NumberBox({ value, min, max, step = 1, title, onChange }: { value: number; min: number; max: number; step?: number; title?: string; onChange: (n: number) => void }) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const commit = () => {
    const n = Number(text);
    if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n)));
    else setText(String(value));
  };
  return (
    <input type="number" value={text} min={min} max={max} step={step} title={title} style={{ ...INPUT, width: 64 }}
      onChange={(e) => setText(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }} />
  );
}

// ── the grid ──────────────────────────────────────────────────────────────

function WindowGrid({
  rows, pro, rank, unit, selected, onPick,
}: {
  rows: SeasWindow[];
  pro: boolean;
  rank: string;
  unit: string;
  selected: string | null;
  onPick: (w: SeasWindow) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null);
  const sorted = useMemo(() => (sort ? sortWindows(rows, sort.key, sort.dir) : rows), [rows, sort]);
  const by = (key: SortKey) =>
    setSort((s) => ({ key, dir: s?.key === key ? ((-s.dir) as 1 | -1) : key === "symbol" || key === "offset" ? 1 : -1 }));
  const cols: [SortKey, string, string][] = [
    ["symbol", "Symbol", ""],
    ["dir", "", "L = long, S = short"],
    ["offset", "Start", "start date · trading days from the From date"],
    ["len", "Period", "how long the window runs from its start"],
    ["hit", "Hit %", "% of years the window was up (down, for a short); hover a cell for the count"],
    ["edge", "Avg %", "mean return over the window"],
    ...(pro
      ? ([
          ["xsHit", "Xs %", "% of years the window beat that year's own average move — a boom or a bear year cancels out"],
          ["xsSharpe", "X Sharpe", "Excess Sharpe: how much, and how steadily, the window beat each year's own normal move; length-adjusted. The Season ranking. ≥1.5 strong · 1–1.5 good · ≈0 no edge"],
        ] as [SortKey, string, string][])
      : []),
  ];
  return (
    <div style={SCROLL}>
      <table style={TABLE}>
        <thead>
          <tr>
            {cols.map(([k, t, tip]) => (
              <th key={k} title={tip || undefined} style={{ ...TH, textAlign: k === "symbol" ? "left" : "right" }} onClick={() => by(k)}>
                {t}
                {sort?.key === k ? (sort.dir === 1 ? " ▴" : " ▾") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((w) => {
            const tone = w.dir === "L" ? "var(--qf-positive)" : "var(--qf-negative)";
            const s = startCell(w);
            const key = rowKey(w);
            return (
              <tr key={key} onClick={() => onPick(w)} style={key === selected ? ROW_ON : undefined}>
                <td style={{ ...TD, textAlign: "left", fontWeight: 700 }}>{w.symbol}</td>
                <td style={{ ...TD, color: tone, fontWeight: 700, fontSize: 11 }}>{w.dir}</td>
                <td style={TD}>
                  {s.date} {s.note && <span style={MUTED}>{s.note}</span>}
                </td>
                <td style={TD}>{w.len}{unit}</td>
                <td style={{ ...TD, position: "relative" }} title={`${Math.round((w.hit / 100) * w.yearsUsed)} of ${w.yearsUsed} years`}>
                  <i style={{ ...BAR, width: `${w.hit}%`, background: tone }} />
                  {Math.round(w.hit)}%
                </td>
                <td style={{ ...TD, color: w.edge > 0 ? "var(--qf-positive)" : "var(--qf-negative)" }}>
                  {w.edge > 0 ? "+" : ""}{w.edge.toFixed(1)}
                </td>
                {pro && (
                  <>
                    <td style={{ ...TD, position: "relative" }}>
                      <i style={{ ...BAR, width: `${w.xsHit ?? 0}%`, background: "var(--qf-accent)" }} />
                      {w.xsHit == null ? "" : `${Math.round(w.xsHit)}%`}
                    </td>
                    <td style={{ ...TD, fontWeight: rank === "season" ? 700 : undefined, color: (w.xsSharpe ?? 0) >= 1 ? "var(--qf-positive)" : undefined }}>
                      {w.xsSharpe == null ? "" : w.xsSharpe.toFixed(2)}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── the calendar ──────────────────────────────────────────────────────────

function Calendar({
  answer, unit, selected, onPick,
}: {
  answer: WindowsAnswer;
  unit: string;
  selected: string | null;
  onPick: (w: SeasWindow) => void;
}) {
  const cells = useMemo(() => calendarCells(answer.from, answer.rows), [answer]);
  return (
    <div style={{ ...SCROLL, padding: 0 }}>
      <div style={CAL}>
        {["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => (
          <div key={d} style={CAL_HEAD}>{d}</div>
        ))}
        {cells.map((c) => (
          <div key={c.date} style={{ ...CAL_DAY, ...(c.isFrom ? CAL_FROM : {}), ...(c.date < answer.from ? CAL_PAST : {}) }}>
            <div style={MUTED}>{shortDate(c.date)}</div>
            {c.windows.slice(0, 4).map((w) => (
              <span key={rowKey(w)} onClick={() => onPick(w)}
                style={{ ...CAL_ENTRY, color: w.dir === "L" ? "var(--qf-positive)" : "var(--qf-negative)",
                  fontWeight: rowKey(w) === selected ? 700 : undefined }}>
                {w.symbol} {w.len}{unit} {w.up}/{w.yearsUsed - w.up} {w.edge > 0 ? "+" : ""}{w.edge.toFixed(1)}%
              </span>
            ))}
            {c.windows.length > 4 && <span style={MUTED}>+{c.windows.length - 4} more</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

const ROOT: CSSProperties = { display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0, background: "var(--qf-background)" };
const BODY: CSSProperties = { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 };
const NOTICE: CSSProperties = { padding: 8, fontSize: 12, color: "var(--qf-text-muted)" };
const ROW: CSSProperties = { display: "flex", alignItems: "center", gap: 4, padding: "5px 8px", borderBottom: "1px solid var(--qf-border)", fontSize: 12, flexWrap: "wrap" };
const COUNT: CSSProperties = { display: "flex", justifyContent: "space-between", gap: 8, padding: "3px 8px", fontSize: 11.5, color: "var(--qf-text-muted)", borderBottom: "1px solid var(--qf-border)" };
const HINT: CSSProperties = { opacity: 0.8 };
const SEG: CSSProperties = { display: "inline-flex", border: "1px solid var(--qf-border)", borderRadius: 6, overflow: "hidden", flexShrink: 0 };
const SEG_BTN: CSSProperties = { border: 0, background: "transparent", padding: "3px 9px", font: "inherit", fontSize: 12, color: "var(--qf-text)", cursor: "pointer", whiteSpace: "nowrap" };
const SEG_SEP: CSSProperties = { borderLeft: "1px solid var(--qf-border)" };
const SEG_ON: CSSProperties = { background: "var(--qf-accent)", color: "var(--qf-on-accent, #fff)" };
const SEG_OFF: CSSProperties = { opacity: 0.45, cursor: "not-allowed" };
const CHIP: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 6, padding: "3px 7px", background: "transparent", color: "var(--qf-text)", font: "inherit", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" };
const CHIP_ON: CSSProperties = { borderColor: "var(--qf-accent)" };
const POP: CSSProperties = {
  position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20, display: "grid", gridTemplateColumns: "auto auto",
  gap: "8px 10px", alignItems: "center", padding: "10px 12px", minWidth: "max-content",
  background: "var(--qf-surface)", border: "1px solid var(--qf-border)", borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
};
const LBL: CSSProperties = { fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--qf-text-muted)" };
const INPUT: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 5, background: "var(--qf-background)", color: "var(--qf-text)", padding: "2px 5px", font: "inherit", fontSize: 12 };
const BTN: CSSProperties = { ...INPUT, cursor: "pointer" };
const SCROLL: CSSProperties = { flex: "1 1 auto", minHeight: 0, overflow: "auto" };
const TABLE: CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 12, fontVariantNumeric: "tabular-nums", color: "var(--qf-text)" };
const TH: CSSProperties = { position: "sticky", top: 0, zIndex: 1, padding: "4px 6px", whiteSpace: "nowrap", cursor: "pointer", fontWeight: 600, color: "var(--qf-text-muted)", background: "var(--qf-surface)", borderBottom: "1px solid var(--qf-border)" };
const TD: CSSProperties = { padding: "3px 6px", textAlign: "right", whiteSpace: "nowrap", borderBottom: "1px solid var(--qf-border)", cursor: "pointer" };
const ROW_ON: CSSProperties = { background: "var(--qf-selection, rgba(88,166,255,0.15))" };
const MUTED: CSSProperties = { color: "var(--qf-text-muted)", fontSize: 10.5 };
const BAR: CSSProperties = { position: "absolute", left: 0, top: 3, bottom: 3, borderRadius: 2, opacity: 0.22 };
const CAL: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 1, background: "var(--qf-border)" };
const CAL_HEAD: CSSProperties = { background: "var(--qf-surface)", textAlign: "center", fontSize: 11, color: "var(--qf-text-muted)", padding: "3px 0" };
const CAL_DAY: CSSProperties = { background: "var(--qf-background)", minHeight: 88, padding: "3px 4px", fontSize: 11, overflow: "hidden", display: "flex", flexDirection: "column" };
const CAL_FROM: CSSProperties = { outline: "2px solid var(--qf-accent)", outlineOffset: -2 };
const CAL_PAST: CSSProperties = { opacity: 0.5 };
const CAL_ENTRY: CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer", fontVariantNumeric: "tabular-nums" };
